import React, { useState } from 'react';
import { Form, Row, Col, Input, Button } from 'reactstrap';
import Select from 'react-select';
import { ModalButtonBuilder, ToastBuilder } from '../../../../../../../../../GlobalComponents/Builders';
import { buildOptionsFromArray, parseJSONSafe } from '../../../../../../../../../GlobalComponents/Helpers';

/**
 * CompositeChainBuilder — Structured builder for mapping_options chain configuration.
 *
 * Provides a UI to define source_chain and destination_chain config,
 * then serializes to JSON and injects into the mapping options textarea.
 *
 * Output format:
 * {
 *   "source_chain": {
 *     "type": "association_table",
 *     "boards": [199, 200],
 *     "chain_labels": ["Type", "SubType", "Item"],
 *     "chain_levels": ["type", "subtype", "item"]
 *   },
 *   "destination_chain": {
 *     "chain_labels": ["Category", "Subcategory", "Item"],
 *     "chain_resources": [1045, 1046, 1047]
 *   }
 * }
 */

const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    multiValue: (base) => ({ ...base, fontSize: 13, padding: '2px 4px' }),
    multiValueLabel: (base) => ({ ...base, fontSize: 13 }),
};

const SOURCE_CHAIN_TYPES = [
    { value: 'none', label: 'None (Destination Only)' },
    { value: 'association_table', label: 'Association Table' },
];

const CompositeChainBuilder = ({ currentValue, onApply, readOnly, jobResources, destinationConnectionId }) => {

    // Source chain state
    const [sourceType, setSourceType] = useState(null);
    const [sourceBoards, setSourceBoards] = useState('');
    const [sourceChainLabels, setSourceChainLabels] = useState('');
    const [sourceChainLevels, setSourceChainLevels] = useState('');

    // Destination chain state
    const [destChainResources, setDestChainResources] = useState([]);
    const [destChainLabels, setDestChainLabels] = useState('');

    const destinationResources = jobResources?.[destinationConnectionId]?.selected_resources || [];
    const destResourceOptions = buildOptionsFromArray(
        destinationResources.map(r => ({ ...r, resource_id: String(r.resource_id) })),
        { label: 'name', value: 'resource_id' }
    );

    const loadFromCurrent = () => {
        const raw = typeof currentValue === 'function' ? currentValue() : currentValue;
        const parsed = typeof raw === 'string' ? parseJSONSafe(raw) : raw;

        if (!parsed || typeof parsed !== 'object') {
            resetFields();
            return;
        }

        // Load source chain
        if (parsed.source_chain) {
            const sc = parsed.source_chain;
            setSourceType(sc.type || null);
            setSourceBoards(Array.isArray(sc.boards) ? sc.boards.join(', ') : '');
            setSourceChainLabels(Array.isArray(sc.chain_labels) ? sc.chain_labels.join(', ') : '');
            setSourceChainLevels(Array.isArray(sc.chain_levels) ? sc.chain_levels.join(', ') : '');
        } else {
            setSourceType('none');
            setSourceBoards('');
            setSourceChainLabels('');
            setSourceChainLevels('');
        }

        // Load destination chain
        if (parsed.destination_chain) {
            const dc = parsed.destination_chain;
            setDestChainResources(Array.isArray(dc.chain_resources) ? dc.chain_resources.map(String) : []);
            setDestChainLabels(Array.isArray(dc.chain_labels) ? dc.chain_labels.join(', ') : '');
        } else {
            setDestChainResources([]);
            setDestChainLabels('');
        }
    };

    const resetFields = () => {
        setSourceType('none');
        setSourceBoards('');
        setSourceChainLabels('');
        setSourceChainLevels('');
        setDestChainResources([]);
        setDestChainLabels('');
    };

    const handleApply = () => {
        const result = {};

        // Build source_chain
        if (sourceType && sourceType !== 'none') {
            const sourceChain = { type: sourceType };

            if (sourceType === 'association_table') {
                if (sourceBoards.trim()) {
                    sourceChain.boards = sourceBoards.split(',').map(b => parseInt(b.trim())).filter(n => !isNaN(n));
                }
                if (sourceChainLabels.trim()) {
                    sourceChain.chain_labels = sourceChainLabels.split(',').map(l => l.trim()).filter(Boolean);
                }
                if (sourceChainLevels.trim()) {
                    sourceChain.chain_levels = sourceChainLevels.split(',').map(l => l.trim()).filter(Boolean);
                }
            }

            result.source_chain = sourceChain;
        }

        // Build destination_chain
        if (destChainResources.length > 0 || destChainLabels.trim()) {
            const destChain = {};
            if (destChainResources.length > 0) {
                destChain.chain_resources = destChainResources.map(r => parseInt(r)).filter(n => !isNaN(n));
            }
            if (destChainLabels.trim()) {
                destChain.chain_labels = destChainLabels.split(',').map(l => l.trim()).filter(Boolean);
            }
            result.destination_chain = destChain;
        }

        if (Object.keys(result).length === 0) {
            onApply?.('');
        } else {
            onApply?.(JSON.stringify(result, null, 2));
        }

        ToastBuilder('info', "Don't forget to Submit to save", {}, 3000);
    };

    return (
        <ModalButtonBuilder
            title='Chain Builder'
            type='button'
            className='btn-info btn-sm'
            buttonStyle={{ minWidth: 110 }}
            modalTitle='Composite Chain Configuration'
            formId='composite-chain-builder-form'
            style={{ width: 600, maxWidth: 600 }}
            onSubmit={readOnly ? false : 'Apply'}
            onCancelLabel={readOnly ? 'Close' : 'Cancel'}
            onClick={loadFromCurrent}
            body={
                <Form id='composite-chain-builder-form' className='input-sm' onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); handleApply(); }}>

                    {/* Source Chain */}
                    <div className='section-group-accent' style={{ borderRadius: 6, padding: '10px 14px' }}>
                        <span className='section-group-title' style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 10 }}>Source Chain</span>

                        <Row className='mb-2'>
                            <Col md={4} className='d-flex align-items-center'><label style={{ fontSize: 12, marginBottom: 0 }}>Type</label></Col>
                            <Col md={8}>
                                <div className='react-select-sm'>
                                    <Select
                                        className='react-select-container'
                                        classNamePrefix='react-select'
                                        options={SOURCE_CHAIN_TYPES}
                                        value={SOURCE_CHAIN_TYPES.find(o => o.value === sourceType) || null}
                                        onChange={(opt) => setSourceType(opt?.value || 'none')}
                                        isDisabled={readOnly}
                                        styles={selectStyles}
                                        menuPortalTarget={document.body}
                                        menuPosition='fixed'
                                        placeholder='Select type...'
                                    />
                                </div>
                            </Col>
                        </Row>

                        {sourceType === 'association_table' && (
                            <>
                                <Row className='mb-2'>
                                    <Col md={4} className='d-flex align-items-center'><label style={{ fontSize: 12, marginBottom: 0 }}>Boards</label></Col>
                                    <Col md={8}>
                                        <Input
                                            bsSize='sm'
                                            type='text'
                                            value={sourceBoards}
                                            onChange={(e) => setSourceBoards(e.target.value)}
                                            readOnly={readOnly}
                                            placeholder='199, 200, 332'
                                            style={{ fontSize: 12 }}
                                        />
                                        <small className='text-muted' style={{ fontSize: 10 }}>Comma-separated board IDs</small>
                                    </Col>
                                </Row>
                                <Row className='mb-2'>
                                    <Col md={4} className='d-flex align-items-center'><label style={{ fontSize: 12, marginBottom: 0 }}>Chain Labels</label></Col>
                                    <Col md={8}>
                                        <Input
                                            bsSize='sm'
                                            type='text'
                                            value={sourceChainLabels}
                                            onChange={(e) => setSourceChainLabels(e.target.value)}
                                            readOnly={readOnly}
                                            placeholder='Type, SubType, Item'
                                            style={{ fontSize: 12 }}
                                        />
                                    </Col>
                                </Row>
                                <Row className='mb-2'>
                                    <Col md={4} className='d-flex align-items-center'><label style={{ fontSize: 12, marginBottom: 0 }}>Chain Levels</label></Col>
                                    <Col md={8}>
                                        <Input
                                            bsSize='sm'
                                            type='text'
                                            value={sourceChainLevels}
                                            onChange={(e) => setSourceChainLevels(e.target.value)}
                                            readOnly={readOnly}
                                            placeholder='type, subtype, item'
                                            style={{ fontSize: 12 }}
                                        />
                                        <small className='text-muted' style={{ fontSize: 10 }}>Internal level keys</small>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </div>

                    {/* Destination Chain */}
                    <div className='section-group-accent' style={{ borderRadius: 6, padding: '10px 14px', marginTop: 10 }}>
                        <span className='section-group-title' style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 10 }}>Destination Chain</span>

                        <Row className='mb-2'>
                            <Col md={4} className='d-flex align-items-center'><label style={{ fontSize: 12, marginBottom: 0 }}>Chain Resources</label></Col>
                            <Col md={8}>
                                <div className='react-select-sm'>
                                    <Select
                                        className='react-select-container react-select-multi'
                                        classNamePrefix='react-select'
                                        isMulti
                                        options={destResourceOptions}
                                        value={destResourceOptions.filter(o => destChainResources.includes(o.value))}
                                        onChange={(opts) => setDestChainResources(opts ? opts.map(o => o.value) : [])}
                                        isDisabled={readOnly}
                                        styles={selectStyles}
                                        menuPortalTarget={document.body}
                                        menuPosition='fixed'
                                        placeholder='Select resources in chain order...'
                                    />
                                </div>
                                <small className='text-muted' style={{ fontSize: 10 }}>Destination resources in order (parent → child)</small>
                            </Col>
                        </Row>
                        <Row className='mb-2'>
                            <Col md={4} className='d-flex align-items-center'><label style={{ fontSize: 12, marginBottom: 0 }}>Chain Labels</label></Col>
                            <Col md={8}>
                                <Input
                                    bsSize='sm'
                                    type='text'
                                    value={destChainLabels}
                                    onChange={(e) => setDestChainLabels(e.target.value)}
                                    readOnly={readOnly}
                                    placeholder='Category, Subcategory, Item'
                                    style={{ fontSize: 12 }}
                                />
                            </Col>
                        </Row>
                    </div>

                </Form>
            }
        />
    );
};

export default CompositeChainBuilder;
